import {
  ChatroomComponent_getMessagesOfChatroom,
  ChatroomComponent_getMessagesOfChatroomVariables,
} from './__generated__/ChatroomComponent_getMessagesOfChatroom';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Input,
  OnDestroy, HostBinding,
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {Apollo, gql} from 'apollo-angular';
import {BehaviorSubject, EMPTY, Subject, Subscription} from 'rxjs';
import {
  ChatroomComponent_sendMessage,
  ChatroomComponent_sendMessageVariables,
} from './__generated__/ChatroomComponent_sendMessage';
import {
  ChatroomComponent_setSeen,
  ChatroomComponent_setSeenVariables,
} from './__generated__/ChatroomComponent_setSeen';
import {exhaustMap, switchMap} from 'rxjs/operators';
import {ChatMessage} from './chat-message';
import {TranslateService} from '@ngx-translate/core';

// Messages as ordered as lifo (last in first out)
const GET_MESSAGES_OF_CHATROOM = gql`
  query ChatroomComponent_getMessagesOfChatroom($input: ID!) {
    chatRoom(id: $input) {
      id
      messages {
        edges {
          node {
            id
            message
            created
            unreadContentForUser
            sentBy {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`;

const SET_SEEN = gql`
  mutation ChatroomComponent_setSeen($ids: [ID]!) {
    trackAction(
      input: { action: Read, actionObjectContentType: ChatMessageType, actionObjectPks: $ids }
    ) {
      found
    }
  }
`;

// Send a message in the room
const SEND_MESSAGE = gql`
  mutation ChatroomComponent_sendMessage($message: String!, $chatRoomId: ID!) {
    sendMessage(message: $message, chatRoomId: $chatRoomId) {
      message {
        id
        message
        created
        unreadContentForUser
        sentBy {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

@Component({
  selector: 'app-chatroom',
  templateUrl: './chatroom.component.html',
  styleUrls: ['./chatroom.component.scss'],
})
export class ChatroomComponent implements OnInit, OnDestroy {
  @ViewChild('messageBox') 'messageBox': ElementRef;
  @Output() seen = new EventEmitter<string>();
  @HostBinding('class.chatroom-container') container = true;

  private chatroomId$ = new BehaviorSubject<string | null>(null);
  @Input() set chatroomId(id: string | null) {
    this.chatroomId$.next(id);
  }
  get chatroomId() {
    return this.chatroomId$.value;
  }

  @Input() currentUserId: string | null = null;
  @Input() showWelcomeMessage = true;

  chatForm = new FormGroup({
    chatInput: new FormControl(null),
  });

  error: string | null = null;
  messages: ChatMessage[] = [];
  sendMessage$ = new Subject<ChatroomComponent_sendMessageVariables>();
  viewMessages$ = new Subject<ChatroomComponent_setSeenVariables>();
  subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.chatroomId$
        .pipe(
          switchMap(id => {
            if (!id) {
              return EMPTY;
            }

            return this.apollo.watchQuery<
              ChatroomComponent_getMessagesOfChatroom,
              ChatroomComponent_getMessagesOfChatroomVariables
            >({
              query: GET_MESSAGES_OF_CHATROOM,
              variables: {input: id},
            }).valueChanges;
          }),
        )
        .subscribe(output => {
          if (!output.data.chatRoom) {
            this.error = this.translate.instant('ERROR_NO_CHATROOM');
            return;
          }

          this.messages = output.data.chatRoom.messages.edges.map(m => m.node).reverse();
          this.setSeen();
          this.scrollDown();
        }),
      this.sendMessage$
        .pipe(
          exhaustMap(input =>
            this.apollo.mutate<
              ChatroomComponent_sendMessage,
              ChatroomComponent_sendMessageVariables
            >({
              mutation: SEND_MESSAGE,
              variables: input,
              update: cache =>
                cache.evict({
                  id: `ChatRoomType:${this.chatroomId}`,
                  fieldName: 'messages',
                }),
            }),
          ),
        )
        .subscribe(),

      this.viewMessages$
        .pipe(
          switchMap(input =>
            this.apollo.mutate<ChatroomComponent_setSeen, ChatroomComponent_setSeenVariables>({
              mutation: SET_SEEN,
              variables: input,
            }),
          ),
        )
        .subscribe(),
    );
  }

  onChangeInput(): void {
    this.scrollDown();
  }

  onPressEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey === false) {
      event.preventDefault();
      event.stopPropagation();
      this.onSendMessage();
    }
  }

  onSendMessage(): void {
    const currentInput = this.chatForm.get('chatInput')?.value;
    if (!currentInput || !this.chatroomId) {
      return;
    }
    this.sendMessage$.next({
      message: currentInput,
      chatRoomId: this.chatroomId,
    });

    this.chatForm.reset();
  }

  scrollDown(): void {
    // Must happen after (async) message fetching.
    setTimeout(() => {
      this.messageBox.nativeElement.scrollTop = this.messageBox.nativeElement.scrollHeight;
    });
  }

  setSeen(): void {
    const ids = this.messages.filter(m => m.unreadContentForUser).map(m => m.id);
    this.viewMessages$.next({ids: ids});

    if (this.chatroomId) {
      this.seen.emit(this.chatroomId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
